import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { CatalogAccessMode } from '../../../types/catalog';
import { useCatalogEditor } from './useCatalogEditor';
import { useCatalogEditorNavigation } from './useCatalogEditorNavigation';

export default function CatalogEditorPage() {
  const { id, catalogId } = useParams<{ id: string; catalogId?: string }>();
  const companyId = id ? parseInt(id) : null;
  const parsedCatalogId = catalogId ? parseInt(catalogId) : null;

  const [emailInput, setEmailInput] = useState('');
  const [copyResult, setCopyResult] = useState<'success' | 'error' | null>(null);

  const { data, form, actions, isValid } = useCatalogEditor({
    companyId,
    catalogId: parsedCatalogId,
  });

  const { actions: navigationActions } = useCatalogEditorNavigation({
    companyId: companyId ?? 0,
    onProductsSelected: actions.handleProductsSelected,
  });

  const handleAddEmail = () => {
    actions.handleAddAccessEmail(emailInput);
    setEmailInput('');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/catalogs/${parsedCatalogId}/view`);
      setCopyResult('success');
    } catch (err) {
      console.error('Failed to copy catalog link:', err);
      setCopyResult('error');
    }
  };

  const handleCloseCopyResult = () => {
    setCopyResult(null);
  };

  if (data.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={actions.handleCancel}
          size="small"
        >
          До каталогів
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {parsedCatalogId ? 'Редагувати каталог' : 'Створити каталог'}
        </Typography>
      </Box>

      {parsedCatalogId && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LinkIcon />}
            onClick={handleCopyLink}
          >
            Скопіювати посилання
          </Button>
        </Box>
      )}

      {data.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {data.error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Назва каталогу"
        value={form.name}
        onChange={(e) => form.setName(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Typography variant="h6" gutterBottom>
        Доступ
      </Typography>
      <RadioGroup
        value={form.accessMode}
        onChange={(e) => form.setAccessMode(e.target.value as CatalogAccessMode)}
        sx={{ mb: 3 }}
      >
        <FormControlLabel
          value={CatalogAccessMode.PUBLIC}
          control={<Radio />}
          label="Публічний — доступ за посиланням, без перевірки одержувача"
        />
        <FormControlLabel
          value={CatalogAccessMode.RESTRICTED}
          control={<Radio />}
          label="Обмежений — лише вказані email-адреси"
        />
      </RadioGroup>

      {form.accessMode === CatalogAccessMode.RESTRICTED && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Email-адреси з доступом
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              label="Email"
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
            />
            <Button
              color="inherit"
              startIcon={<AddIcon />}
              onClick={handleAddEmail}
              disabled={!emailInput.trim()}
            >
              Додати
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {form.accessEmails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => actions.handleRemoveAccessEmail(email)}
              />
            ))}
          </Box>
          {form.accessEmails.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Додайте хоча б одну email-адресу
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h6">
          Товари
        </Typography>
        <Button
          color="inherit"
          startIcon={<AddIcon />}
          onClick={navigationActions.handleOpenProductPicker}
        >
          Додати товари
        </Button>
      </Box>

      {form.lineItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Товарів ще немає — додайте хоча б один
        </Typography>
      ) : (
        <Box sx={{ mb: 3 }}>
          {form.lineItems.map((item) => (
            <Box
              key={item.productId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography variant="body1" noWrap>
                  {item.productName}
                </Typography>
                {item.productCode && (
                  <Typography variant="body2" color="text.secondary">
                    {item.productCode}
                  </Typography>
                )}
              </Box>
              <TextField
                size="small"
                label="Ціна, USD"
                type="number"
                value={item.unitPrice}
                onChange={(e) => actions.handleUnitPriceChange(item.productId, e.target.value)}
                sx={{ width: 140 }}
              />
              <IconButton
                aria-label="Видалити"
                onClick={() => actions.handleRemoveLineItem(item.productId)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={actions.handleSubmit}
          disabled={!isValid || data.saving}
          startIcon={data.saving ? <CircularProgress size={16} /> : undefined}
        >
          Зберегти
        </Button>
        <Button
          color="inherit"
          onClick={actions.handleCancel}
          disabled={data.saving}
        >
          Скасувати
        </Button>
      </Box>

      <Snackbar
        open={copyResult !== null}
        autoHideDuration={3000}
        onClose={handleCloseCopyResult}
      >
        <Alert
          severity={copyResult === 'success' ? 'success' : 'error'}
          onClose={handleCloseCopyResult}
        >
          {copyResult === 'success' ? 'Посилання скопійовано' : 'Не вдалося скопіювати посилання'}
        </Alert>
      </Snackbar>
    </Box>
  );
}
